import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarClock, Layers3, Replace } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const fmt = (value) => value ? new Date(`${value}T12:00:00`).toLocaleDateString('it-IT') : '-';

const OPTIONS = [
  { key: 'sum', label: 'Somma gli sconti', detail: 'Somma lineare: -10% + -20% = -30%.', Icon: Layers3 },
  { key: 'replace', label: 'Sostituisci le promozioni attuali', detail: 'Le precedenti terminano il giorno prima della nuova e non riprendono.', Icon: Replace },
  { key: 'postpone', label: 'Posticipa la nuova', detail: 'Sposta l’intero periodo dopo la promozione che termina più tardi.', Icon: CalendarClock },
];

export const PromoOverlapResolutionDialog = ({ open, conflict, busy, onCancel, onConfirm }) => {
  const [choice, setChoice] = useState('');
  useEffect(() => { if (open) setChoice(''); }, [open]);
  const rows = conflict?.conflicts || [];
  const proposed = conflict?.proposed || {};
  const sum = useMemo(() => Number(proposed.pct || 0) + rows.reduce((total, row) => total + Number(row.pct || 0), 0), [proposed.pct, rows]);
  const losesResidualDays = rows.some(row => row.extends_beyond_new);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !busy && onCancel()}>
      <DialogContent className="max-w-2xl bg-white border-slate-200 shadow-xl" data-testid="promo-overlap-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <AlertTriangle className="h-5 w-5 text-amber-600" /> Sovrapposizione sulle date di check-in
          </DialogTitle>
          <DialogDescription data-testid="promo-overlap-message">
            Scegli una sola regola: verrà applicata a tutte le {rows.length} promozioni sovrapposte.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="overlap-promo-card-new">
          <p className="text-xs font-semibold uppercase text-blue-700">Nuova promozione</p>
          <p className="text-sm font-semibold text-slate-900">{proposed.property_code} · {proposed.property_name}</p>
          <p className="text-sm text-slate-600">-{proposed.pct}% · check-in {fmt(proposed.stay_from)} → {fmt(proposed.stay_to)}</p>
        </div>

        <div className="max-h-44 space-y-2 overflow-y-auto" data-testid="overlap-existing-promotions-list">
          {rows.map((row) => (
            <div key={row.promo_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 p-3"
              data-testid={`overlap-existing-promo-${row.promo_id}`}>
              <div><p className="text-sm font-semibold text-slate-900">Promozione -{row.pct}%</p>
                <p className="text-xs text-slate-500">Check-in {fmt(row.stay_from)} → {fmt(row.stay_to)}</p></div>
              <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">Conflitto {fmt(row.overlap_from)} → {fmt(row.overlap_to)}</span>
            </div>
          ))}
        </div>

        <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Gestione sovrapposizione">
          {OPTIONS.map(({ key, label, detail, Icon }) => {
            const disabled = key === 'postpone' && conflict?.postpone_blocked;
            return <button key={key} type="button" role="radio" aria-checked={choice === key} disabled={disabled}
              onClick={() => setChoice(key)} data-testid={`overlap-choice-${key}`}
              className={`min-h-32 rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${choice === key ? 'border-blue-600 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-400'}`}>
              <Icon className={`mb-2 h-5 w-5 ${choice === key ? 'text-blue-700' : 'text-slate-500'}`} />
              <p className="text-sm font-semibold text-slate-900">{label}</p>
              <p className="mt-1 text-xs text-slate-500">{key === 'sum' ? `Totale risultante: -${sum}%` : detail}</p>
              {disabled && <p className="mt-1 text-xs font-semibold text-rose-600">Non disponibile: esiste una promo senza scadenza.</p>}
            </button>;
          })}
        </div>

        {choice === 'replace' && losesResidualDays && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="overlap-replace-disclaimer">
            <b>Attenzione:</b> almeno una promozione sostituita durava più della nuova. Verrà troncata definitivamente e non riprenderà: i giorni residui successivi andranno persi.
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="overlap-cancel-btn">Annulla</Button>
          <Button onClick={() => onConfirm(choice)} disabled={!choice || busy} data-testid="overlap-confirm-resolution-btn">
            {busy ? 'Applico…' : 'Conferma decisione'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
